import { Fetcher } from '@this/src/util';
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Link } from '@this/shared/ui/navigations/link';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { Button } from '@this/shared/ui/inputs/button';
import type { CreditBalanceHistoryResponse } from '@this/domain/credit_balance_history';
import CreditBalanceHistory, {
  convertCreditBalanceHistoryResponseToArgs
} from '@this/domain/credit_balance_history';
import { ExpensesMonthlyPriceOrder } from '@this/src/domain/expenses/monthly_price_order';
import type { ExpensesMonthlyPriceOrderArgs } from '@this/src/domain/expenses/monthly_price_order';
import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import MainTitle from '../main_title';
import { ContentBodyDetail } from '../organization';
import type { PaymentType, PlanType, DowngradeSchedule, ChurnReasonCode } from './plan_types';
import { PlanCreditHistoryTable } from './plan_credit_history_table';
import { ProPlanSection } from './plan_pro_section';
import {
  PlanUpgradeModal,
  PlanDowngradeModal,
  PlanCancelDowngradeModal,
  PlanCancelModal,
  PlanChurnModal
} from './plan_modals';

interface Response {
  payment_type: PaymentType | null;
  current_plan: string | null;
  current_plan_type: PlanType | null;
  contract_expire_at: string | null;
  downgrade_schedule: DowngradeSchedule | null;
  member_count: number;
  standard_price_per_user: number;
  pro_price_per_user: number;
  stripe_pk: string;
  credit_balance_histories: CreditBalanceHistoryResponse[];
  monthly_price_orders: ExpensesMonthlyPriceOrderArgs[];
  churn_available: boolean;
}

const Plans = observer(() => {
  const styles = getStyles();
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [currentPlanType, setCurrentPlanType] = useState<PlanType | null>(null);
  const [contractExpireAt, setContractExpireAt] = useState<string | null>(null);
  const [downgradeSchedule, setDowngradeSchedule] = useState<DowngradeSchedule | null>(null);
  const [paymentType, setPaymentType] = useState<PaymentType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [memberCount, setMemberCount] = useState<number>(1);
  const [standardPricePerUser, setStandardPricePerUser] = useState<number>(0);
  const [proPricePerUser, setProPricePerUser] = useState<number>(0);
  const [historyRecords, setHistoryRecords] = useState<CreditBalanceHistory[]>([]);
  const [monthlyPriceOrders, setMonthlyPriceOrders] = useState<ExpensesMonthlyPriceOrder[]>([]);
  const [showPriceDetail, setShowPriceDetail] = useState<boolean>(false);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType>('standard');
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState<boolean>(false);
  const [downgradeSubmitting, setDowngradeSubmitting] = useState<boolean>(false);
  const [downgradeError, setDowngradeError] = useState<string | null>(null);
  const [showCancelDowngradeModal, setShowCancelDowngradeModal] = useState<boolean>(false);
  const [cancelDowngradeSubmitting, setCancelDowngradeSubmitting] = useState<boolean>(false);
  const [cancelDowngradeError, setCancelDowngradeError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [cancelSubmitting, setCancelSubmitting] = useState<boolean>(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [showChurnModal, setShowChurnModal] = useState<boolean>(false);
  const [churnSubmitting, setChurnSubmitting] = useState<boolean>(false);
  const [churnError, setChurnError] = useState<string | null>(null);
  const [churnAvailable, setChurnAvailable] = useState<boolean>(false);
  const planChangeManagedByAdmin = paymentType === 'order';

  const fetchData = async () => {
    setLoading(true);
    const res = await Fetcher.get<Response>('/aiexpense/organization/plans.json');
    setLoading(false);
    setCurrentPlan(res.current_plan);
    setCurrentPlanType(res.current_plan_type);
    setContractExpireAt(res.contract_expire_at);
    setDowngradeSchedule(res.downgrade_schedule);
    setPaymentType(res.payment_type);
    setMemberCount(res.member_count);
    setStandardPricePerUser(res.standard_price_per_user);
    setProPricePerUser(res.pro_price_per_user);
    setHistoryRecords(
      res.credit_balance_histories.map(h => new CreditBalanceHistory(convertCreditBalanceHistoryResponseToArgs(h)))
    );
    setMonthlyPriceOrders(res.monthly_price_orders.map(raw => new ExpensesMonthlyPriceOrder(raw)));
    setChurnAvailable(res.churn_available);
    if (!stripePromise) {
      setStripePromise(loadStripe(res.stripe_pk));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenUpgrade = (planType: PlanType) => {
    setSelectedPlanType(planType);
    setShowUpgradeModal(true);
  };

  const handleConfirmDowngrade = async () => {
    setDowngradeSubmitting(true);
    setDowngradeError(null);
    try {
      await Fetcher.patch('/aiexpense/organization/plans/downgrade.json', {});
      setShowDowngradeModal(false);
      window.location.reload();
    } catch (e) {
      setDowngradeError(
        e && e.response?.data
          ? e.response.data.message
          : '通信エラーが発生しました。時間をおいて再度お試しください。'
      );
    }
    setDowngradeSubmitting(false);
  };

  const handleCancelDowngrade = async () => {
    setCancelDowngradeSubmitting(true);
    setCancelDowngradeError(null);
    try {
      await Fetcher.delete('/aiexpense/organization/plans/downgrade.json', {});
      setShowCancelDowngradeModal(false);
      window.location.reload();
    } catch (e) {
      setCancelDowngradeError(
        e && e.response?.data
          ? e.response.data.message
          : '通信エラーが発生しました。時間をおいて再度お試しください。'
      );
    }
    setCancelDowngradeSubmitting(false);
  };

  const handleConfirmCancel = async () => {
    setCancelSubmitting(true);
    setCancelError(null);
    try {
      await Fetcher.delete('/aiexpense/organization/plans.json', {});
      setShowCancelModal(false);
      window.location.reload();
    } catch (e) {
      setCancelError(
        e && e.response?.data
          ? e.response.data.message
          : '通信エラーが発生しました。時間をおいて再度お試しください。'
      );
    }
    setCancelSubmitting(false);
  };

  const handleConfirmChurn = async (reasonCode: ChurnReasonCode, reasonDetail: string | null) => {
    setChurnSubmitting(true);
    setChurnError(null);
    try {
      await Fetcher.post('/aiexpense/organization/plans/churn.json', {
        reason_code: reasonCode,
        reason_detail: reasonDetail
      });
      window.location.href = '/aiexpense/churn_completed';
    } catch (e) {
      setChurnError(
        e && e.response?.data
          ? e.response.data.message
          : '通信エラーが発生しました。時間をおいて再度お試しください。'
      );
      setChurnSubmitting(false);
    }
  };

  return (
    <>
      <MainTitle value="利用プラン" />
      <ContentBodyDetail>
        {loading ? (
          <Loading />
        ) : (
          <>
            <div style={styles.section}>
              <div style={styles.planTitle}>現在のプラン</div>
              <ul style={styles.planCurrent}>{currentPlan}</ul>
              {contractExpireAt && (
                <div style={styles.renewalDate}>
                  次回更新日:
                  {new Date(contractExpireAt).toLocaleDateString('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              )}
            </div>
            {memberCount === 0 && (
              <div style={styles.section}>
                <div style={styles.noMemberWarning}>
                  有効なユーザーが見つかりません。組織の設定を確認してください。
                </div>
              </div>
            )}
            {!paymentType && (
              <div style={styles.section}>
                <div style={styles.planOptions}>
                  <div style={styles.planOption}>
                    <Button
                      style={styles.button}
                      onClick={() => handleOpenUpgrade('standard')}
                      disabled={memberCount === 0}
                    >
                      AI Expense Standard にアップグレード
                    </Button>
                    <ul style={styles.ul}>
                      <li style={styles.li}>月額{standardPricePerUser}円（税込、1ユーザあたり）</li>
                    </ul>
                  </div>
                  <div style={styles.planOption}>
                    <Button
                      style={styles.button}
                      onClick={() => handleOpenUpgrade('pro')}
                      disabled={memberCount === 0}
                    >
                      AI Expense Pro にアップグレード
                    </Button>
                    <ul style={styles.ul}>
                      <li style={styles.li}>月額{proPricePerUser}円（税込、1ユーザあたり）</li>
                      <li style={styles.li}>OCR機能をご利用いただけます</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {currentPlanType === 'standard' && !planChangeManagedByAdmin && (
              <div style={styles.section}>
                <Button
                  style={styles.button}
                  onClick={() => handleOpenUpgrade('pro')}
                  disabled={memberCount === 0}
                >
                  AI Expense Pro にアップグレード
                </Button>
                <ul style={styles.ul}>
                  <li style={styles.li}>月額{proPricePerUser}円（税込、1ユーザあたり）</li>
                  <li style={styles.li}>OCR機能をご利用いただけます</li>
                  <li style={styles.li}>※スタンダードプランとの差額（残り日数に応じた日割り）を請求します</li>
                </ul>
              </div>
            )}
            {currentPlanType === 'pro' && !planChangeManagedByAdmin && paymentType !== null && (
              <div style={styles.section}>
                <ProPlanSection
                  downgradeSchedule={downgradeSchedule}
                  standardPricePerUser={standardPricePerUser}
                  onOpenDowngradeModal={() => setShowDowngradeModal(true)}
                  onOpenCancelDowngradeModal={() => setShowCancelDowngradeModal(true)}
                />
              </div>
            )}
            {planChangeManagedByAdmin && (
              <div style={styles.section}>
                <div style={styles.description}>プラン変更は管理者へ依頼してください。</div>
              </div>
            )}
            {historyRecords.length > 0 && (
              <div style={styles.section}>
                <h3>履歴</h3>
                <PlanCreditHistoryTable records={historyRecords} />
              </div>
            )}
            {monthlyPriceOrders.length > 0 && (
              <div style={styles.section}>
                <h3>履歴</h3>
                <div style={styles.description}>
                  <div>当アカウントは請求書払いによる決済でご利用いただいております。</div>
                  <div>
                    発行済みの請求書は
                    <Link href="/organization/invoices" isExternal>
                      こちら
                    </Link>
                    よりご確認ください。
                  </div>
                </div>
                <table style={styles.table}>
                  <tbody>
                    <tr>
                      <th style={styles.th}>品目</th>
                      <th style={styles.th}>単価</th>
                      <th style={styles.th}>人数</th>
                      <th style={styles.th}>請求月</th>
                      <th style={styles.thRight}>価格</th>
                      <th style={styles.thRight}>合計（税込）</th>
                    </tr>
                    {monthlyPriceOrders.map(r => (
                      <tr key={r.id}>
                        <td style={styles.td}>AI Expense利用料{r.title}</td>
                        <td style={styles.td}>{r.formattedPriceUnit()}</td>
                        <td style={styles.td}>{r.formattedUserCount()}</td>
                        <td style={styles.td}>{r.formattedBillingTarget()}</td>
                        <td style={styles.tdRight}>{r.formattedPriceWithoutTax()}</td>
                        <td style={styles.tdRight}>{r.formattedPriceWithTax()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {paymentType !== 'order' && (
              <div style={styles.section}>
                {showPriceDetail ? (
                  <>
                    <Link onClick={() => setShowPriceDetail(false)}>▼料金詳細</Link>
                    <div style={styles.priceDescriptionBlock}>
                      <div style={styles.priceDescriptionSection}>
                        <h3>概要</h3>
                        <ul style={styles.ul}>
                          <li style={styles.li}>
                            AI Expense Standard
                            は、1ユーザーあたり月額200円（税抜き）で、OCR以外の全機能がご利用いただけるプランです。
                          </li>
                          <li style={styles.li}>
                            AI Expense Pro
                            は、1ユーザーあたり月額400円（税抜き）で、OCR機能を含む全機能がご利用いただけるプランです。
                          </li>
                          <li style={styles.li}>
                            登録ユーザー数の増減の際は、日割りで実際にご利用いただいた日数に応じて料金が発生します。
                          </li>
                          <li style={styles.li}>
                            クレジットカードの引き落とし回数を可能な限り更新時の月1回に抑えるため、「クレジットポイント」という仕組みを採用しています。ユーザー減少の際はクレジットポイントとして払い戻し、ユーザーの追加または更新の際にクレジットポイントを利用して料金を支払うことができます。
                          </li>
                          <li style={styles.li}>
                            クレジットポイントには貨幣価値または交換価値がなく、譲渡や返金はできません。また、プランのキャンセルに伴い失効します。
                          </li>
                        </ul>
                      </div>
                      <div style={styles.priceDescriptionSection}>
                        <h3>料金の発生と決済のタイミングについて</h3>
                        <ul style={styles.ul}>
                          <li style={styles.li}>
                            サブスクリプションを契約した日に、有効なユーザー分の料金がクレジットカードから引き落とされます。
                          </li>
                          <li style={styles.li}>
                            サブスクリプションをキャンセルしない限り、有効期間の最終日に自動で更新され、クレジットカードから引き落とされます。
                          </li>
                          <li style={styles.li}>
                            サブスクリプションの利用中にユーザーを無効化すると、次の更新日までの日割り料金がクレジットポイント残高に追加されます。
                            <br />
                            例えば、サブスクリプションの契約日または更新日から、次の更新日までの日数が30日で、そのうち15日が経過した時点であるユーザーを無効化すると、そのユーザーに対する支払い済みの月額料金（税込）の50%がクレジットポイント残高に追加されます。
                          </li>
                          <li style={styles.li}>
                            サブスクリプションを利用中に追加のユーザーを招待すると、次の更新日までの日割り料金がクレジットポイント残高からマイナスされます。クレジットポイント残高が不足している場合は、次の更新時に合わせてクレジットカードから引き落とされます。
                          </li>
                          <li style={styles.li}>
                            クレジットポイント残高がプラスの状態でサブスクリプションをキャンセルすると、その時点でクレジットポイント残高が破棄されます。
                          </li>
                          <li style={styles.li}>
                            クレジットポイント残高がマイナスの状態でサブスクリプションをキャンセルすると、その時点で不足分がクレジットカードから引き落とされます。
                          </li>
                        </ul>
                      </div>
                    </div>
                  </>
                ) : (
                  <Link onClick={() => setShowPriceDetail(true)}>▶料金詳細</Link>
                )}
              </div>
            )}
            {(paymentType === 'contract' || churnAvailable) && (
              <div style={styles.section}>
                {paymentType === 'contract' && (
                  <Link onClick={() => setShowCancelModal(true)}>サブスクリプションをキャンセル</Link>
                )}
                {churnAvailable && (
                  <div style={paymentType === 'contract' ? styles.churnLink : undefined}>
                    <Link onClick={() => setShowChurnModal(true)}>解約手続きへ</Link>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </ContentBodyDetail>
      <PlanUpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        selectedPlanType={selectedPlanType}
        memberCount={memberCount}
        standardPricePerUser={standardPricePerUser}
        proPricePerUser={proPricePerUser}
        currentPlanType={currentPlanType}
        stripePromise={stripePromise}
        onPaymentSucceeded={() => {
          setShowUpgradeModal(false);
          window.location.reload();
        }}
      />
      <PlanDowngradeModal
        open={showDowngradeModal}
        onClose={() => setShowDowngradeModal(false)}
        submitting={downgradeSubmitting}
        error={downgradeError}
        onConfirm={handleConfirmDowngrade}
      />
      <PlanCancelDowngradeModal
        open={showCancelDowngradeModal}
        onClose={() => setShowCancelDowngradeModal(false)}
        submitting={cancelDowngradeSubmitting}
        error={cancelDowngradeError}
        onConfirm={handleCancelDowngrade}
      />
      <PlanCancelModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        currentPlan={currentPlan}
        submitting={cancelSubmitting}
        error={cancelError}
        onConfirm={handleConfirmCancel}
      />
      <PlanChurnModal
        open={showChurnModal}
        onClose={() => setShowChurnModal(false)}
        submitting={churnSubmitting}
        error={churnError}
        onConfirm={handleConfirmChurn}
      />
    </>
  );
});

export default Plans;

function getStyles() {
  const buttonBase: React.CSSProperties = { width: 'fit-content', borderRadius: '4px', marginBottom: '12px' };
  const thBase: React.CSSProperties = { fontWeight: 'normal', color: '#7d7d7d', padding: '4px' };
  const tdBase: React.CSSProperties = { padding: '4px' };
  return {
    section: { marginBottom: '40px' } as React.CSSProperties,
    planTitle: {
      fontWeight: 300,
      fontSize: '1.5em',
      lineHeight: '28px',
      paddingBottom: '10px'
    } as React.CSSProperties,
    planCurrent: { fontSize: '1.125em' } as React.CSSProperties,
    renewalDate: { fontSize: '14px', color: '#666', marginTop: '4px' } as React.CSSProperties,
    noMemberWarning: { color: '#a72c09', fontSize: '14px' } as React.CSSProperties,
    planOptions: { display: 'flex', gap: '24px' } as React.CSSProperties,
    planOption: { flex: 1 } as React.CSSProperties,
    button: buttonBase,
    ul: { listStyleType: 'disc', paddingLeft: '25px', marginTop: '10px' } as React.CSSProperties,
    li: { marginBottom: '5px', fontWeight: 300, fontSize: '1.125em' } as React.CSSProperties,
    description: { marginBottom: '20px' } as React.CSSProperties,
    table: { margin: 0, tableLayout: 'auto', width: 'auto', fontSize: '12px' } as React.CSSProperties,
    th: thBase,
    thRight: { ...thBase, textAlign: 'right' } as React.CSSProperties,
    td: tdBase,
    tdRight: { ...tdBase, textAlign: 'right', minWidth: '100px' } as React.CSSProperties,
    priceDescriptionBlock: {
      border: '1px dotted #7d7d7d',
      padding: '10px',
      marginTop: '5px'
    } as React.CSSProperties,
    priceDescriptionSection: { marginBottom: '20px' } as React.CSSProperties,
    churnLink: { marginTop: '12px' } as React.CSSProperties
  } as const;
}
